import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import type { FortisTransactionData } from '../../types'

const COMMERCE_JS_URL = 'https://js.fortis.tech/commercejs-v1.0.0.min.js'

export interface FortisPaymentProps {
  clientToken: string
  environment: 'sandbox' | 'production'
  onSuccess: (data: FortisTransactionData) => void
  onError: (error: string) => void
  showSubmitButton?: boolean
}

export interface FortisPaymentHandle {
  submit: () => void
}

interface CommerceElements {
  create: (options: {
    container: string
    environment: string
    theme: string
    showSubmitButton: boolean
    showReceipt: boolean
  }) => void
  on: (event: string, callback: (event?: { data?: Record<string, unknown> }) => void) => void
  submit: () => void
  destroy: () => void
}

declare global {
  interface Window {
    Commerce?: {
      elements: new (clientToken: string) => CommerceElements
    }
  }
}

function loadCommerceScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Commerce) {
      resolve()
      return
    }
    const existing = document.querySelector(`script[src="${COMMERCE_JS_URL}"]`) as HTMLScriptElement | null
    if (existing) {
      // Script tag exists but may still be loading
      if (window.Commerce) {
        resolve()
      } else {
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', () => reject(new Error('Failed to load Commerce.js')))
      }
      return
    }
    const script = document.createElement('script')
    script.src = COMMERCE_JS_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Commerce.js'))
    document.head.appendChild(script)
  })
}

export const FortisPayment = forwardRef<FortisPaymentHandle, FortisPaymentProps>(
  function FortisPayment({ clientToken, environment, onSuccess, onError, showSubmitButton = false }, ref) {
    const containerRef = useRef<HTMLDivElement>(null)
    const elementsRef = useRef<CommerceElements | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Keep latest callbacks in refs so the effect doesn't re-run when they change
    const onSuccessRef = useRef(onSuccess)
    const onErrorRef = useRef(onError)
    useEffect(() => { onSuccessRef.current = onSuccess }, [onSuccess])
    useEffect(() => { onErrorRef.current = onError }, [onError])

    // Expose submit() to parent via ref
    useImperativeHandle(ref, () => ({
      submit() {
        elementsRef.current?.submit()
      },
    }), [])

    useEffect(() => {
      let cancelled = false

      async function init() {
        setLoading(true)
        setError(null)

        try {
          await loadCommerceScript()
        } catch {
          if (!cancelled) {
            setError('Unable to load secure payment fields. Please refresh the page.')
            setLoading(false)
          }
          return
        }

        if (cancelled || !window.Commerce || !containerRef.current) return

        // Clean up previous instance
        if (elementsRef.current) {
          try { elementsRef.current.destroy() } catch { /* ignore */ }
          elementsRef.current = null
        }
        containerRef.current.innerHTML = ''

        try {
          const elements = new window.Commerce.elements(clientToken)

          elements.create({
            container: '#fortis-payment-container',
            environment: environment === 'sandbox' ? 'sandbox' : 'production',
            theme: 'default',
            showSubmitButton,
            showReceipt: false,
          })

          elements.on('ready', () => {
            if (!cancelled) setLoading(false)
          })

          // Commerce.js has no working destroy(): every instance keeps its window
          // "message" listener for the life of the page, so an element mounted
          // for an earlier, abandoned transaction still receives 'done' when the
          // member pays through a later one. Ignore anything from an instance
          // whose effect has been cleaned up (2026-09-02 duplicate stock order).
          elements.on('done', (event) => {
            if (cancelled) return
            const data = event?.data || {}
            const statusCode = Number(data.status_code ?? data.status_id)
            if (statusCode === 102 && data.id) {
              onSuccessRef.current({
                id: data.id as string,
                status_code: statusCode,
                account_holder_name: data.account_holder_name as string | undefined,
                last_four: data.last_four as string | undefined,
              })
            } else {
              onErrorRef.current('The payment was not authorized. Please review your details and try again.')
            }
          })

          elements.on('validationError', () => {
            if (cancelled) return
            onErrorRef.current('Please review your payment details.')
          })

          elements.on('error', () => {
            if (cancelled) return
            onErrorRef.current('There was a problem with the payment. Please try again.')
          })

          elements.on('tokenExpired', () => {
            if (!cancelled) {
              setError('Your session has expired. Please refresh the page to restart.')
            }
          })

          elementsRef.current = elements
        } catch {
          if (!cancelled) {
            setError('Unable to initialize secure payment fields.')
            setLoading(false)
          }
        }

        // Fallback: if 'ready' event never fires, clear loading after timeout
        setTimeout(() => {
          if (!cancelled) setLoading(false)
        }, 5000)
      }

      init()

      return () => {
        cancelled = true
        if (elementsRef.current) {
          try { elementsRef.current.destroy() } catch { /* ignore */ }
          elementsRef.current = null
        }
      }
    }, [clientToken, environment, showSubmitButton])

    if (error) {
      return (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 text-sm">
          {error}
        </div>
      )
    }

    return (
      <div>
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
            <span className="ml-3 text-text-muted text-sm">Loading secure payment form...</span>
          </div>
        )}
        <div
          id="fortis-payment-container"
          ref={containerRef}
          className={loading ? 'hidden' : ''}
        />
      </div>
    )
  }
)
